/**
 * useDisposalOfflineQueue.js
 * ──────────────────────────
 * React hook providing a clean interface for queuing disposal operations
 * to a dedicated IndexedDB namespace: 'tmg-disposal-queue'.
 *
 * Auto-drains on mount (if online) and on connectivity restore.
 * 3-attempt retry buffer: failures 1-2 stay pending, 3+ marked failed.
 */

import { useCallback, useEffect } from 'react';

import { apiFetch } from '../auth/apiFetch';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:7071/api';
const DB_NAME = 'tmg-disposal-queue';
const DB_VERSION = 1;
const STORE_NAME = 'disposalQueue';

/* ── IndexedDB helpers ────────────────────────────────────────────────── */

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('by_status', 'status', { unique: false });
      }
    };
    req.onsuccess = (e) => resolve(e.target.result);
    req.onerror = (e) => reject(e.target.error);
  });
}

function dbPut(db, item) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const req = store.put(item);
    req.onsuccess = () => resolve();
    req.onerror = (e) => reject(e.target.error);
  });
}

function dbGetAll(db) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const req = store.getAll();
    req.onsuccess = (e) => resolve(e.target.result);
    req.onerror = (e) => reject(e.target.error);
  });
}

function dbDelete(db, id) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const req = store.delete(id);
    req.onsuccess = () => resolve();
    req.onerror = (e) => reject(e.target.error);
  });
}

function generateId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/* ── Monotonic counter for strict insertion order ────────────────────── */
let _queueSeq = 0;

/* ── Queue item factory ───────────────────────────────────────────────── */

function createQueueItem(action, endpoint, payload) {
  return {
    id: generateId(),
    action,
    endpoint,
    payload,
    status: 'pending',
    retry_count: 0,
    queued_at: new Date().toISOString(),
    seq: ++_queueSeq,
  };
}

/* ── API call dispatcher ──────────────────────────────────────────────── */

async function dispatchItem(item, callerEmail) {
  const body = { ...item.payload, action: item.action, caller_email: callerEmail };
  const response = await apiFetch(`${API_BASE_URL}/${item.endpoint}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `${item.endpoint} ${item.action} failed: ${response.status}`);
  }
  return response.json().catch(() => ({ status: 'ok' }));
}

/* ── Module-level drain lock (shared across all hook instances) ───────── */
let isDraining = false;

/* ── Hook ─────────────────────────────────────────────────────────────── */

export function useDisposalOfflineQueue(callerEmail) {

  /* ── Enqueue helpers ────────────────────────────────────────────────── */

  const enqueue = useCallback(async (action, endpoint, payload) => {
    const db = await openDB();
    const item = createQueueItem(action, endpoint, payload);
    await dbPut(db, item);
    return item.id;
  }, []);

  const queueBolCreate = useCallback((payload) => {
    return enqueue('CREATE', 'manage_bol', payload);
  }, [enqueue]);

  const queueAddTool = useCallback((payload) => {
    return enqueue('ADD_TOOL', 'manage_bol', payload);
  }, [enqueue]);

  const queueRemoveTool = useCallback((payload) => {
    return enqueue('REMOVE_TOOL', 'manage_bol', payload);
  }, [enqueue]);

  const queueBolClose = useCallback((payload) => {
    return enqueue('CLOSE', 'manage_bol', payload);
  }, [enqueue]);

  const queueBolCloseAmended = useCallback((payload) => {
    return enqueue('CLOSE_AMENDED', 'manage_bol', payload);
  }, [enqueue]);

  const queueAddPhoto = useCallback((payload) => {
    return enqueue('ADD_PHOTO', 'manage_bol', payload);
  }, [enqueue]);

  const queueFrostDismissal = useCallback((payload) => {
    return enqueue('DISMISS', 'manage_frost_dismissal', payload);
  }, [enqueue]);

  /* ── Drain queue ────────────────────────────────────────────────────── */

  const drainDisposalQueue = useCallback(async (email) => {
    const drainEmail = email || callerEmail;
    if (!drainEmail) return { synced: 0, failed: 0 };
    if (!navigator.onLine) return { synced: 0, failed: 0 };
    if (isDraining) return { synced: 0, failed: 0 };

    isDraining = true;
    try {
      const db = await openDB();
      const items = await dbGetAll(db);
      const pending = items
        .filter(i => i.status === 'pending' || i.status === 'failed')
        .sort((a, b) => (a.seq || 0) - (b.seq || 0));

      if (pending.length === 0) return { synced: 0, failed: 0 };

      console.log(`[disposalQueue] draining ${pending.length} item(s)...`);
      let synced = 0;
      let failed = 0;

      for (const item of pending) {
        try {
          await dispatchItem(item, drainEmail);
          await dbDelete(db, item.id);
          synced++;
          console.log(`[disposalQueue] synced: ${item.action} ${item.id}`);
        } catch (err) {
          item.retry_count = (item.retry_count || 0) + 1;
          item.status = item.retry_count >= 3 ? 'failed' : 'pending';
          item.error = err.message;
          await dbPut(db, item);
          failed++;
          console.error(`[disposalQueue] failed ${item.id} (${item.action}):`, err.message);
        }
      }

      console.log(`[disposalQueue] drain complete: ${synced} synced, ${failed} failed`);
      return { synced, failed };
    } finally {
      isDraining = false;
    }
  }, [callerEmail]);

  /* ── Status ─────────────────────────────────────────────────────────── */

  const getDisposalQueueStatus = useCallback(async () => {
    const db = await openDB();
    const items = await dbGetAll(db);
    return {
      pending: items.filter(i => i.status === 'pending').length,
      failed: items.filter(i => i.status === 'failed').length,
    };
  }, []);

  /* ── TRIGGER 1: Auto-drain on mount + connectivity restore ──────────── */

  useEffect(() => {
    if (!callerEmail) return;

    // Drain on mount if online
    if (navigator.onLine) {
      drainDisposalQueue(callerEmail);
    }

    // Drain on connectivity restore
    function handleOnline() {
      console.log('[disposalQueue] connectivity restored, draining...');
      drainDisposalQueue(callerEmail);
    }
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [callerEmail, drainDisposalQueue]);

  return {
    queueBolCreate,
    queueAddTool,
    queueRemoveTool,
    queueBolClose,
    queueBolCloseAmended,
    queueAddPhoto,
    queueFrostDismissal,
    drainDisposalQueue,
    getDisposalQueueStatus,
  };
}
